import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';

import { UserService } from '@core/services/user.service';
import { AuthService } from '@core/services/auth.service';
import { User } from '@core/models/user';
import { CompanyService } from '@core/services/company.service';
import { TenableChildAccount } from '@core/tenable/models/tenable-child-account';
import { ThemeService } from '@core/services/theme.service';
import { AuthFlowService } from '@core/services/auth-flow.service';

export const adminAuthGuard: CanActivateFn = async (
  _route,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const companyService = inject(CompanyService);
  const themeService = inject(ThemeService);
  const router = inject(Router);
  const authFlowService = inject(AuthFlowService);
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);

  // If APP_INITIALIZER flagged a bootstrap error, redirect to error page immediately
  const bootstrapError = isBrowser ? sessionStorage.getItem('bootstrapError') : null;
  if (bootstrapError) {
    return router.createUrlTree(['/error']);
  }

  // Wait for the centralized auth service to finish its first hydration pass.
  // The old `user(auth).pipe(take(1))` logic was racing page refreshes and Stripe
  // returns, which dumped authenticated users back to /login.
  await authService.waitForAuthReady(10000);

  let userModel = await authService.waitForHydratedUser(10000);

  // If user model not loaded after timeout, try localStorage fallback
  if (!userModel) {
    const sessionUser = await authService.getUserSession();
    if (sessionUser && sessionUser instanceof User) {
      userModel = sessionUser;
    }
  }

  if (!userModel || !userModel.email) {
    authFlowService.rememberRedirectUrl(state.url);
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url },
    });
  }

  // Remember the entry URL. This guard sits on the parent `dashboard` route and
  // does not re-run for child navigations, so AdminLayoutComponent refreshes the
  // value on every NavigationEnd (#730).
  authFlowService.rememberRedirectUrl(state.url);

  // No email-verification wall here (#943): an unverified account reaches
  // the plan step, pays and runs its first scan. The dashboard shows a
  // "Verify your email" banner instead, and only inviting teammates and
  // exporting reports require verification (enforced by the API and rules).

  userService.setUserModel(userModel); // Refresh user model
  authService.setUserSession(userModel); // Refresh user session

  companyService.user = userModel;
  companyService.setUserProfile();

  const storedAccountDetails = isBrowser ? sessionStorage.getItem('accountDetails') : null;
  if (storedAccountDetails) {
    // If Account Details are present, check if its expiration is valid
    const accountDetails: TenableChildAccount =
      JSON.parse(storedAccountDetails);
    if (
      accountDetails.license_expiration_date &&
      new Date(accountDetails.license_expiration_date * 1000) < new Date()
    ) {
      const licenseExpirationDate: Date = new Date(
        accountDetails.license_expiration_date * 1000,
      );
      themeService.triggerError(
        ` Your license expired on ${licenseExpirationDate.toDateString()}. Please contact your account administrator to renew your license.`,
      );
      await authService.signOutUser();
      // Fail hard: redirect to error page to avoid navigation stall
      return router.createUrlTree(['/error']);
    }
    return true; // Account details are present, no need to check for expiration
  }

  const accountIsExpired: boolean = await getEffectiveAccountExpirationStatus(
    userModel,
    companyService,
    authService,
  );
  return handleAuthenticatedUser(router, accountIsExpired);
};

const handleAuthenticatedUser = async (
  router: Router,
  accountIsExpired: boolean,
): Promise<boolean | UrlTree> => {
  if (accountIsExpired) {
    // Non-free expired accounts should be directed to the account reinstatement page
    return router.createUrlTree(['/account/expired']);
  }

  return true;
};

type UserWithIntendedTier = User & {
  /**
   * Intended tier for pre-signup / onboarding flows where the company
   * record may not yet exist. This allows us to apply tier-based logic
   * before a company is fully created.
   */
  intendedTier?: 'free' | 'trial' | 'premium';
};

/**
 * Computes the effective expiration status for the current account.
 *
 * Free tier users always bypass expiration enforcement.
 */
const getEffectiveAccountExpirationStatus = async (
  user: User,
  companyService: CompanyService,
  authService: AuthService,
): Promise<boolean> => {
  const userWithIntendedTier = user as UserWithIntendedTier;
  const tier = companyService.company?.tier || userWithIntendedTier.intendedTier;

  if (tier === 'free') {
    return false;
  }

  return await authService.isAccountExpired(user);
};
